export interface ActivityReportEventType {
  code: string;
  name: string;
}

export interface ActivityReportEvent {
  id: number;
  title: string;
  scheduledLdt: string; // local datetime string (no Z suffix)
  completedAt: string | null;
  isCompleted: boolean;
  calendar: {
    id: number;
    name: string;
  };
  eventType: ActivityReportEventType;
}

export interface MeasurementUnit {
  id: number;
  code: string;
  name: string;
  symbol: string;
}

export interface MeasurementType {
  id: number;
  code: string;
  name: string;
  defaultUnit: MeasurementUnit;
  isActive: boolean;
  sortOrder: number;
}

// Старий плоский формат — використовується в ClientReportsTab (експертський вигляд)
export interface ActivityReportMeasurement {
  id: number;
  userId: number;
  measurementType: MeasurementType;
  unit: MeasurementUnit;
  value: number;
  formattedValue: string;
  measuredAt: string;
  isRecent: boolean;
  createdAt: string;
  updatedAt: string;
}

// Новий згрупований формат — використовується в /reports/activity (клієнтський self-report)
export interface ActivityReportMeasurementEntry {
  id: number;
  value: number;
  formattedValue: string;
  measuredAt: string;
}

export interface ActivityReportMeasurementGroup {
  type: MeasurementType;
  entries: ActivityReportMeasurementEntry[];
}

export interface ActivityReportFile {
  id: number;
  originalFilename: string;
  mimeType: string;
  size: number;
  url: string;
  fileType: 'image' | 'document' | 'other';
  comment: string | null;
  createdAt: string;
}

export interface ActivityReportFood {
  id: number;
  name: string;
  details: string | null;
  category: string | null;
  measurementUnit: string;
  rawMeasurementUnit: string;
  gramsPerUnit: number;
  defaultMeasurementValue: number;
  caloriesPer100g: number;
  proteinPer100g: number;
  fatPer100g: number;
  carbsPer100g: number;
  fiberPer100g: number | null;
  createdByUserId: number;
  isStructured: boolean;
  isSystem: boolean;
  usageCount: number;
}

export interface ActivityReportWorkout {
  id: number;
  name: string;
  category: string | null;
  details: string | null;
  metValue: number | null;
  createdByUserId: number;
  isSystem: boolean;
  usageCount: number;
}

// Для /client-reports/:clientId/activity (експертський вигляд — плоский формат вимірів)
export interface ActivityReport {
  clientId: number;
  period: {
    from: string;
    to: string;
  };
  summary: {
    totalEvents: number;
    totalMeasurements: number;
    totalFiles: number;
    totalFoods: number;
    totalWorkouts: number;
  };
  events: ActivityReportEvent[];
  measurements: ActivityReportMeasurement[];
  files: ActivityReportFile[];
  foods: ActivityReportFood[];
  workouts: ActivityReportWorkout[];
}

// Нова структура events для /reports/activity (клієнтський self-report)
// Групування: тип події → дата → список подій
export interface SelfActivityReportEventDetails {
  rawInput?: string | null;
  totals?: {
    kcal?: number | null;
    protein?: number | null;
    fat?: number | null;
    carbs?: number | null;
    fiber?: number | null;
  } | null;
}

export interface SelfActivityReportEventItem {
  id: number;
  title: string;
  scheduledLdt: string; // local datetime string (no Z suffix)
  completedAt: string | null;
  isCompleted: boolean;
  isOwnEvent: boolean;
  details: SelfActivityReportEventDetails | null;
  expert: { id: number; name: string } | null;
  calendar: { id: number; name: string };
}

export interface SelfActivityReportEventDate {
  date: string; // YYYY-MM-DD
  events: SelfActivityReportEventItem[];
}

export interface SelfActivityReportEventGroup {
  eventType: { id: number; code: string; name: string };
  dates: SelfActivityReportEventDate[];
}

// Довідники з /reports/activity/options
export interface ReportOptionsEventType {
  id: number;
  code: string;
  name: string;
  icon: string;
  color: string;
}

export interface ReportOptionsSection {
  key: string;
  labelKey: string;
}

export interface ReportOptionsPrompt {
  id: number;
  title: string;
  content: string;
}

export interface ReportOptions {
  eventTypes: ReportOptionsEventType[];
  sections: ReportOptionsSection[];
  prompts: ReportOptionsPrompt[];
}

export interface ActivityReportProfile {
  general: {
    bornYear: number | null;
    gender: number | null; // 1 = male, 2 = female
    height: number | null;
    activityLevel: string | null;
    goal: string | null;
    timezone: string | null;
    locale: string | null;
  };
  measurements: {
    weight: string | number | null;
  };
  initialMeasurements: Record<string, string | number | null>;
  medical: {
    complaints: string | null;
    chronic_diseases: string | null;
    family_chronic_diseases: string | null;
    surgeries_injuries: string | null;
  };
  nutrition: {
    dietary_restrictions: string | null;
    favorite_products_dishes: string | null;
    disliked_food: string | null;
    meals_count: number | null;
    snacks_count: number | null;
    daily_liquid_liters: number | null;
    daily_water_liters: number | null;
    favorite_liquid: string | null;
  };
}

export interface ActivityReportMetrics {
  userId?: number;
  bmi: string | number | null;
  bmr: number | null;
  amr: number | null;
  wdn: number | null;
  wdna: number | null;
  whr: string | number | null;
  amdr: number | null;
}

export interface ActivityReportGoals {
  waterMl: number | null;
  caloriesIntake: number | null;
  caloriesBurned: number | null;
  proteinG: number | null;
  fatG: number | null;
  carbsG: number | null;
  createdAt: string;
  updatedAt: string;
}

// Останній запис по кожному типу вимірювань (незалежно від звітного періоду)
export interface LatestMeasurementEntry {
  id: number;
  measurementType: {
    id: number;
    code: string;
    name: string;
    defaultUnit: { symbol: string };
  };
  formattedValue: string;
  measuredAt: string;
}

export interface ReportAppointment {
  id: number;
  expertName: string;
  scheduledAt: string; // UTC ISO string
  durationMinutes: number;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  outcome: string | null;
}

export const APPOINTMENT_STATUS_LABELS: Record<ReportAppointment['status'], string> = {
  pending: 'Очікує підтвердження',
  confirmed: 'Підтверджено',
  completed: 'Завершено',
  cancelled: 'Скасовано',
};

export const APPOINTMENT_STATUS_COLORS: Record<ReportAppointment['status'], 'warning' | 'info' | 'success' | 'error'> = {
  pending: 'warning',
  confirmed: 'info',
  completed: 'success',
  cancelled: 'error',
};

export interface ReportQuestionnaireAnswer {
  question: string;
  answer: string;
}

export interface ReportQuestionnaire {
  surveyId: number;
  templateId: number;
  templateName: string;
  completedAt: string;
  answers: ReportQuestionnaireAnswer[];
}

// Для /reports/activity (клієнтський self-report)
// Секції можуть бути відсутні якщо не були запитані через sections[]
export interface SelfActivityReport {
  clientId: number;
  clientName: string;
  timezone: string;
  period: {
    from: string;
    to: string;
  };
  sections: string[];
  summary: {
    totalEvents: number;
    totalMeasurements: number;
    totalFiles: number;
    totalAppointments?: number;
  };
  profile?: ActivityReportProfile;
  metrics?: ActivityReportMetrics;
  goals?: ActivityReportGoals;
  events?: SelfActivityReportEventGroup[];
  measurements?: ActivityReportMeasurementGroup[];
  latestMeasurements?: LatestMeasurementEntry[];
  files?: ActivityReportFile[];
  appointments?: ReportAppointment[];
  questionnaires?: ReportQuestionnaire[];
}

export interface AiReportListItem {
  id: number;
  reportType: string;
  periodFrom: string;
  periodTo: string;
  sections: string[] | null;
  status: 'pending' | 'completed' | 'failed';
  createdAt: string;
}

export interface AiReportDetail extends AiReportListItem {
  response: string | null;
}

// Тип AI-звіту (`nest_ai_reports.report_type`, бекенд - AiReportType). Спільні підписи для кабінету клієнта,
// експерта й адмінського журналу `/admin/ai_reports`; невідомий тип показується як є.
export const AI_REPORT_TYPE_LABELS: Record<string, string> = {
  'activity-analysis': 'Аналіз активності',
  'ehi-explanation': "Пояснення Індексу здоров'я",
};
